/**
 * Responsive Sidebar — Full RBAC-enforced
 *
 * Every menu item has a `permission` or `anyOf` guard.
 * `filterMenuItems` hides items the user cannot access.
 * BottomTabBar also respects permissions.
 */
import { useEffect, useRef } from 'react'
import { Layout, Menu, Drawer, Badge } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  DashboardOutlined,
  ClusterOutlined, BankOutlined, BuildOutlined, ApartmentOutlined,
  SettingOutlined, NumberOutlined, ShopOutlined, TagOutlined, HomeOutlined,
  ShoppingCartOutlined, PieChartOutlined, FileProtectOutlined, TruckOutlined,
  FormOutlined, MailOutlined, ProfileOutlined, ReconciliationOutlined,
  SolutionOutlined, CarOutlined, CheckSquareOutlined, RollbackOutlined,
  AccountBookOutlined, StarOutlined, CustomerServiceOutlined, NodeIndexOutlined,
  LockOutlined, DownloadOutlined, LoginOutlined, UsergroupAddOutlined,
  UserAddOutlined, UserDeleteOutlined, LogoutOutlined, AlertOutlined, CompassOutlined,
  KeyOutlined, RetweetOutlined, VideoCameraOutlined, ClearOutlined, ContactsOutlined,
  DatabaseOutlined, UnorderedListOutlined, BookOutlined, FileDoneOutlined,
  GoldOutlined, AppstoreOutlined, ExperimentOutlined, LayoutOutlined,
  PlayCircleOutlined, ControlOutlined,
  SafetyCertificateOutlined, FileSearchOutlined, AuditOutlined, WarningOutlined,
  TrophyOutlined,
  ThunderboltOutlined, MergeCellsOutlined, ScheduleOutlined, SwapOutlined,
  PartitionOutlined, CalculatorOutlined, DeleteOutlined,
  SearchOutlined,
  TeamOutlined, IdcardOutlined, CalendarOutlined, TagsOutlined, FallOutlined,
  WalletOutlined, PrinterOutlined, ScanOutlined, CloudServerOutlined,
  CoffeeOutlined,
  ShoppingOutlined, RocketOutlined,
  FileTextOutlined, PercentageOutlined, CheckCircleOutlined, FundOutlined,
  BarChartOutlined, RiseOutlined, ClockCircleOutlined, DollarOutlined, LineChartOutlined,
  DotChartOutlined, FileExcelOutlined,
  UserOutlined, SafetyOutlined,
  AppstoreAddOutlined, MenuOutlined,
  PushpinOutlined,
  QrcodeOutlined,
  ImportOutlined,
  FundProjectionScreenOutlined,
  LinkOutlined,
  ToolOutlined,
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { useAuthStore } from '@/features/auth/store'

const { Sider } = Layout

// ── Permission-aware menu item type ───────────────────────────────────────────
type MenuItemDef = {
  key: string
  icon?: React.ReactNode
  label: string | React.ReactNode
  permission?: string      // user must have this permission
  anyOf?: string[]         // user must have at least one of these
  children?: MenuItemDef[]
  /** AntD Menu item-type. Use 'group' to render a non-clickable section
   *  divider with its own children (visual sub-grouping inside a submenu). */
  type?: 'group'
}

// ── Menu items — every item has permission or anyOf guard ─────────────────────
const menuItems: MenuItemDef[] = [
  {
    // Dashboard: hidden from pure operational roles (gate keeper, etc.)
    // Visible to anyone with management/admin/reporting level access
    key: '/', icon: <DashboardOutlined />, label: 'Dashboard',
    anyOf: ['company.read', 'user.read', 'mis.read', 'production.read', 'grn.read', 'quality.read', 'payroll.read', 'po.create'],
  },

  {
    key: 'masters', icon: <ClusterOutlined />, label: 'Masters',
    // Admin-only master data (company/machine/count/shift) OR operational master access
    // (supplier/item/store). plant.read and department.read are given to ALL roles for
    // the header — excluded here so Gate Entry Operators don't see Masters.
    anyOf: [
      'company.read', 'machine.read', 'count.read', 'shift.read',
      'supplier.read', 'item.read', 'store.read',
    ],
    children: [
      // System masters — admin-only
      { key: 'masters-grp-system', type: 'group', label: 'System Setup', children: [
        { key: '/masters/numbering-series', label: '#️⃣ Numbering Series', icon: <NumberOutlined />, permission: 'user.read' },
        { key: '/masters/approval-rules',   label: '✅ Edit Approval Rules', icon: <SafetyCertificateOutlined />, permission: 'user.read' },
        { key: '/masters/companies',   label: 'Companies',           icon: <BankOutlined />,      permission: 'company.read' },
        { key: '/masters/plants',      label: 'Plants',              icon: <BuildOutlined />,     permission: 'company.read' },
        { key: '/masters/departments', label: 'Departments',         icon: <ApartmentOutlined />, permission: 'company.read' },
        { key: '/masters/machines',    label: 'Machines',            icon: <SettingOutlined />,   permission: 'machine.read' },
        // Shift Master — required for HR pages (attendance, allocation,
        // worker efficiency, training all need shift_id)
        { key: '/masters/shifts',      label: 'Shifts',              icon: <ClockCircleOutlined />, permission: 'shift.read' },
        { key: '/masters/counts',      label: 'Count Master',        icon: <NumberOutlined />,    permission: 'count.read' },
      ]},
      // Transactional masters — used by Purchase / Stores / GRN
      { key: 'masters-grp-suppliers', type: 'group', label: 'Suppliers & Items', children: [
        { key: '/gate/suppliers',         label: 'Suppliers / Vendors', icon: <ShopOutlined />, permission: 'supplier.read' },
        { key: '/masters/supplier-categories', label: 'Vendor Categories', icon: <TagsOutlined />, permission: 'supplier.read' },
        { key: '/gate/items',             label: 'Item Master',         icon: <TagOutlined />,  permission: 'item.read' },
        { key: '/masters/item-categories', label: 'Item Categories',    icon: <TagsOutlined />, permission: 'item.read' },
        { key: '/gate/stores',            label: 'Stores & Warehouses', icon: <HomeOutlined />, permission: 'store.read' },
      ]},
    ],
  },

  {
    key: 'procurement', icon: <ShoppingCartOutlined />, label: 'Procurement',
    anyOf: ['po.read', 'pr.read', 'rfq.read', 'quotation.read'],
    children: [
      { key: 'proc-grp-txn', type: 'group', label: 'Transactions', children: [
        { key: '/procurement/dashboard',          label: 'Purchase Dashboard',    icon: <PieChartOutlined />,        permission: 'po.read' },
        { key: '/procurement/purchase-orders',    label: 'Purchase Orders',       icon: <FileProtectOutlined />,     permission: 'po.read' },
        { key: '/procurement/delivery-orders',    label: 'Delivery Orders',       icon: <TruckOutlined />,           permission: 'po.read' },
        { key: '/procurement/do-reconciliation',  label: 'DO Reconciliation',     icon: <ReconciliationOutlined />,  permission: 'po.read' },
        { key: '/procurement/requisitions',       label: 'Purchase Requisitions', icon: <FormOutlined />,            permission: 'pr.read' },
        { key: '/procurement/rfqs',               label: 'RFQ',                   icon: <MailOutlined />,            permission: 'rfq.read' },
        { key: '/procurement/quotations',         label: 'Quotations',            icon: <ProfileOutlined />,         permission: 'quotation.read' },
        { key: '/procurement/cs',                 label: 'Comparative Statement', icon: <ReconciliationOutlined />,  permission: 'cs.read' },
        { key: '/procurement/work-orders',        label: 'Work Orders',           icon: <SolutionOutlined />,        permission: 'wo.read' },
        { key: '/procurement/shipment-advices',   label: 'Shipment Advices',      icon: <CarOutlined />,             permission: 'po.read' },
        { key: '/procurement/service-entries',    label: 'Service Entry',         icon: <CheckSquareOutlined />,     permission: 'service_entry.read' },
        { key: '/procurement/rejection-notes',    label: 'Rejection / RTV',       icon: <RollbackOutlined />,        permission: 'rejection.read' },
        { key: '/procurement/invoices',           label: 'Purchase Invoices',     icon: <AccountBookOutlined />,     permission: 'purchase_invoice.read' },
      ]},
      { key: 'proc-grp-setup', type: 'group', label: 'Setup & Config', children: [
        { key: '/procurement/vendor-performance', label: 'Vendor Performance',    icon: <StarOutlined />,            permission: 'vendor_perf.read' },
        { key: '/procurement/service-masters',    label: 'Service Master',        icon: <CustomerServiceOutlined />, permission: 'service_master.read' },
        { key: '/procurement/approval-matrix',    label: 'Approval Matrix',       icon: <NodeIndexOutlined />,       permission: 'approval_matrix.read' },
        { key: '/procurement/tc-templates',       label: 'T&C Templates',         icon: <FileTextOutlined />,        permission: 'po.read' },
      ]},
    ],
  },

  {
    key: 'gate-security', icon: <LockOutlined />, label: 'Gate & Security',
    anyOf: ['gate.read', 'visitor.read', 'weighbridge.read'],
    children: [
      { key: 'gate-grp-gate', type: 'group', label: 'Gate', children: [
        { key: '/gate/dashboard',            label: 'Gate Dashboard',    icon: <PieChartOutlined />,     permission: 'gate.read' },
        { key: '/gate/expected-inward',      label: 'Expected Inward',   icon: <DownloadOutlined />,     permission: 'gate.read' },
        { key: '/fleet/gate-movement',       label: 'Vehicle Movement',  icon: <LogoutOutlined />,       permission: 'vehicle.trip.read' },
        { key: '/gate/entries',              label: 'Inward Gate Entry', icon: <LoginOutlined />,        permission: 'gate.read' },
        { key: '/gate/weighbridge-corrections', label: 'Weighbridge Corrections', icon: <AuditOutlined />, permission: 'weighbridge.read' },
        { key: '/security/contractor-labor', label: 'Contractor Entry',  icon: <UserAddOutlined />,      permission: 'gate.read' },
        { key: '/security/material-outward', label: 'Outward Gate Pass', icon: <LogoutOutlined />,       permission: 'gate.read' },
      ]},
      { key: 'gate-grp-visitors', type: 'group', label: 'Visitors', children: [
        { key: '/gate/visitors',             label: 'Visitor Log',       icon: <UsergroupAddOutlined />, permission: 'visitor.read' },
        { key: '/gate/business-card-library', label: 'Business Card Library', icon: <IdcardOutlined />,  permission: 'visitor.read' },
        { key: '/gate/visitor-approvals',    label: 'My Visitor Approvals',  icon: <SafetyOutlined />,   permission: 'visitor.approve' },
      ]},
      { key: 'gate-grp-security', type: 'group', label: 'Security Ops', children: [
        { key: '/security/incidents',        label: 'Incidents',         icon: <AlertOutlined />,        permission: 'gate.read' },
        { key: '/security/patrol',           label: 'Patrol Logs',       icon: <CompassOutlined />,      permission: 'gate.read' },
        { key: '/security/key-control',      label: 'Key Control',       icon: <KeyOutlined />,          permission: 'gate.read' },
        { key: '/security/shift-handover',   label: 'Shift Handover',    icon: <RetweetOutlined />,      permission: 'gate.read' },
        { key: '/security/cctv-logs',        label: 'CCTV Logs',         icon: <VideoCameraOutlined />,  permission: 'gate.read' },
        { key: '/security/scrap-disposal',   label: 'Scrap Disposal',    icon: <ClearOutlined />,        permission: 'gate.read' },
        { key: '/security/contractors',      label: 'Contractor Master', icon: <ContactsOutlined />,     permission: 'gate.read' },
      ]},
    ],
  },

  {
    key: 'fleet', icon: <CarOutlined />, label: 'Fleet & Vehicles',
    anyOf: ['vehicle.read', 'vehicle.trip.read', 'vehicle.fuel.read', 'vehicle.service.read', 'vehicle.tyre.read', 'vehicle.damage.read', 'vehicle.document.read', 'vehicle.fine.read', 'vehicle.cost.read'],
    children: [
      { key: '/fleet/vehicles', label: 'Vehicles', icon: <CarOutlined />, permission: 'vehicle.read' },
      { key: '/fleet/gate-movement', label: 'Gate Movement', icon: <LogoutOutlined />, permission: 'vehicle.trip.read' },
      { key: '/fleet/drivers', label: 'Drivers', icon: <IdcardOutlined />, permission: 'vehicle.trip.read' },
      { key: '/fleet/fuel', label: 'Fuel & Efficiency', icon: <DashboardOutlined />, permission: 'vehicle.fuel.read' },
      { key: '/fleet/service', label: 'Service & Maintenance', icon: <ToolOutlined />, permission: 'vehicle.service.read' },
      { key: '/fleet/tyres', label: 'Tyres', icon: <DotChartOutlined />, permission: 'vehicle.tyre.read' },
      { key: '/fleet/damage', label: 'Damage & Accidents', icon: <WarningOutlined />, permission: 'vehicle.damage.read' },
      { key: '/fleet/documents', label: 'Documents & Expiry', icon: <FileProtectOutlined />, permission: 'vehicle.document.read' },
      { key: '/fleet/cost-fines', label: 'Cost & Fines', icon: <DollarOutlined />, permission: 'vehicle.fine.read' },
    ],
  },

  {
    key: 'stores', icon: <DatabaseOutlined />, label: 'Stores & Stock',
    anyOf: ['grn.read', 'godown.read', 'store.read', 'bale.read', 'spares.read', 'pr.read', 'quotation.read', 'cs.read', 'location.read', 'bin.adjust', 'spares.scan.issue', 'location.label.print', 'spare.consumption.read', 'spares.reorder.read'],
    children: [
      { key: '/stores/grn',               label: 'GRN',                   icon: <DownloadOutlined />,      permission: 'grn.read' },
      // SPARES-PROC-SEP-001 Task 6 — read-only spare catalog browse
      { key: '/stores/spare-master',      label: 'Spare Master',          icon: <DatabaseOutlined />,      permission: 'spares.read' },
      { key: '/stores/spare-requests',    label: 'Spare Requests',        icon: <ToolOutlined />,          permission: 'spares.read' },
      // SPARE-REORDER-001 — Auto-reorder recommendation review + config
      { key: '/stores/reorder',           label: 'Reorder Engine',        icon: <SyncOutlined />,          permission: 'spares.reorder.read' },
      { key: '/stores/purchase-indents',        label: 'Purchase Indents',        icon: <FileTextOutlined />,        permission: 'pr.read' },
      // SPARES-PROC-SEP-001 Task 7 — buyer worklist deep-linking into the indent flow
      { key: '/stores/spare-buying-queue',      label: 'Spare Buying Queue',      icon: <ProfileOutlined />,         permission: 'pr.read' },
      { key: '/stores/vendor-quotes',           label: 'Vendor Quotes',           icon: <SolutionOutlined />,        permission: 'quotation.read' },
      { key: '/stores/comparative-statements',  label: 'Comparative Statement',   icon: <ReconciliationOutlined />,  permission: 'cs.read' },
      { key: '/stores/spare-purchase-orders',   label: 'Spare Purchase Orders',   icon: <ShoppingOutlined />,       permission: 'po.read' },
      { key: '/stores/repair-jobs',             label: 'Repair / Job-Work',       icon: <ToolOutlined />,            permission: 'repair.read' },
      { key: '/stores/spare-loans',             label: 'Spare Loans',             icon: <SwapOutlined />,            permission: 'spares.loan.read' },
      { key: '/stores/spares-mis',              label: 'Spares MIS',              icon: <PieChartOutlined />,        permission: 'budget.read' },
      { key: '/stores/monthly-budget',          label: 'Monthly Budget',          icon: <WalletOutlined />,          permission: 'budget.read' },
      { key: '/stores/extra-budget-requests',   label: 'Extra-Budget Requests',   icon: <RiseOutlined />,            permission: 'budget.read' },
      { key: '/stores/godown-assignment', label: 'Assign Godown',         icon: <PushpinOutlined />,       permission: 'godown.read' },
      { key: '/stores/godown-approval',   label: 'Godown Approval',       icon: <SafetyOutlined />,        permission: 'godown.read' },
      { key: '/stores/quarantine-zone',   label: 'Quarantine Zone',       icon: <ExperimentOutlined />,    anyOf: ['quality.read', 'godown.read'] },
      { key: '/stores/bales',             label: 'Bale List',             icon: <UnorderedListOutlined />, permission: 'bale.read' },
      { key: '/stores/stock-ledger',      label: 'Stock Ledger',          icon: <BookOutlined />,          permission: 'store.read' },
      { key: '/stores/issue-slips',       label: 'Issue Slips',           icon: <FileDoneOutlined />,      permission: 'store.read' },
      { key: '/stores/material-returns',  label: 'Material Returns',      icon: <RollbackOutlined />,      permission: 'store.read' },
      // SLM-001 A — Inventory Location Management (WMS)
      { key: '/stores/locations',    label: 'Storage Locations',     icon: <PartitionOutlined />,     permission: 'location.read' },
      { key: '/stores/locate',       label: 'Locate / Bin Contents', icon: <SearchOutlined />,         permission: 'location.read' },
      { key: '/stores/cycle-count',  label: 'Cycle Count',           icon: <AuditOutlined />,          permission: 'bin.adjust' },
      // SLM-001 B — QR label printing + scan-to-issue
      { key: '/stores/scan-issue',   label: 'Scan to Issue',         icon: <ScanOutlined />,           permission: 'spares.scan.issue' },
      { key: '/stores/item-labels',  label: 'Item Labels',           icon: <QrcodeOutlined />,         permission: 'location.label.print' },
      // SLM-001 C — Consumption reporting (machine consumption + abnormality watchlist)
      { key: '/stores/consumption', label: 'Machine Consumption',   icon: <BarChartOutlined />,       permission: 'spare.consumption.read' },
      { key: '/stores/abnormality', label: 'Abnormality Watchlist', icon: <WarningOutlined />,         permission: 'spare.consumption.read' },
    ],
  },

  // ── Inventory Ageing (Sprint 0076 Phase 3.2-C) ───────────────────────────
  // Backend-computed buckets, slow-moving, dead-stock, critical-reserved.
  {
    key: 'inventory-ageing', icon: <ClockCircleOutlined />, label: 'Inventory Ageing',
    permission: 'store.read',
    children: [
      { key: '/inventory/ageing',              label: 'Ageing Dashboard',    icon: <DashboardOutlined />,     permission: 'store.read' },
      { key: '/inventory/ageing/item-wise',    label: 'Item-wise Ageing',    icon: <UnorderedListOutlined />, permission: 'store.read' },
      { key: '/inventory/ageing/lot-wise',     label: 'Lot-wise Ageing',     icon: <PartitionOutlined />,     permission: 'store.read' },
      { key: '/inventory/ageing/slow-moving',  label: 'Slow-Moving Stock',   icon: <FallOutlined />,          permission: 'store.read' },
      { key: '/inventory/ageing/dead-stock',   label: 'Dead Stock',          icon: <WarningOutlined />,       permission: 'store.read' },
      { key: '/inventory/ageing/critical',     label: 'Critical Spares',     icon: <SafetyCertificateOutlined />, permission: 'store.read' },
      { key: '/inventory/ageing/high-value',   label: 'High-Value Old',      icon: <DollarOutlined />,        permission: 'store.read' },
      { key: '/inventory/ageing/machine-wise', label: 'Machine-wise Spares', icon: <ToolOutlined />,          permission: 'store.read' },
    ],
  },

  // ── Cotton RM Flow (Phase COTTON-RM-002 Part B) ───────────────────────────
  // Practical operations layer on top of GateEntry / GRN / Bale / StockLedger.
  {
    key: 'cotton-rm', icon: <GoldOutlined />, label: 'Cotton RM Flow',
    permission: 'store.read',
    children: [
      { key: '/inventory/cotton-rm-dashboard',     label: 'RM Control Dashboard', icon: <DashboardOutlined />,         permission: 'store.read' },
      { key: '/inventory/cotton-in-premises',      label: 'In-Premises Cotton',   icon: <SafetyCertificateOutlined />, permission: 'store.read' },
      { key: '/inventory/cotton-movement',         label: 'Cotton Movements',     icon: <SwapOutlined />,              permission: 'store.read' },
      { key: '/inventory/bales/qr-print',          label: 'Bale QR Print',        icon: <PrinterOutlined />,           permission: 'store.read' },
      { key: '/inventory/bales/scan',              label: 'Bale QR Scan',         icon: <ScanOutlined />,              permission: 'store.read' },
      { key: '/quality/cotton-qc-pending',         label: 'QC Pending Report',    icon: <FileSearchOutlined />,        permission: 'quality.read' },
      { key: '/inventory/mixing-readiness',        label: 'Mixing Readiness',     icon: <AuditOutlined />,             permission: 'store.read' },
      { key: '/inventory/cotton-cost-adjustments', label: 'Cost Adjustments',     icon: <DollarOutlined />,            permission: 'store.read' },
      { key: '/inventory/cotton-cost-adjustment-report', label: 'Cost Adj. Report', icon: <FileSearchOutlined />,      permission: 'store.read' },
      { key: '/inventory/cotton-cost-restatements', label: 'Cost Restatements',   icon: <WarningOutlined />,           permission: 'store.read' },
    ],
  },

  {
    key: 'bale-mgmt', icon: <GoldOutlined />, label: 'Bale Management',
    anyOf: ['bale.read', 'quality.read', 'recipe.read'],
    children: [
      // ── Operations ───────────────────────────────────────────────────────
      { key: 'bm-grp-ops', type: 'group', label: 'Operations', children: [
        // Phase BM2 — operator-mode quick laydown. Top so floor staff find it first.
        { key: '/bale-mgmt/quick-laydown',     label: '⚡ Quick Laydown',    icon: <ThunderboltOutlined />, permission: 'bale.read' },
        { key: '/bale-mgmt/mill-lots',         label: 'Mill Lots',          icon: <AppstoreOutlined />,   permission: 'bale.read' },
        { key: '/stores/bales',                label: 'Bale Tracker (QR)',  icon: <QrcodeOutlined />,     permission: 'bale.read' },
        { key: '/bale-mgmt/bale-qc',           label: 'HVI / Bale QC',      icon: <ExperimentOutlined />, permission: 'bale.read' },
        { key: '/bale-mgmt/yarn-tests',        label: 'Yarn Test Entry',    icon: <ExperimentOutlined />, permission: 'quality.read' },
        { key: '/bale-mgmt/mix-recipe-picker', label: '🧪 Mix Recipe Picker', icon: <ThunderboltOutlined />, permission: 'bale.read' },
        { key: '/bale-mgmt/laydown-planning',  label: 'Laydown Planning',   icon: <LayoutOutlined />,     permission: 'bale.read' },
        { key: '/bale-mgmt/pre-conditioning',  label: 'Pre-Conditioning',   icon: <ClockCircleOutlined />, permission: 'bale.read' },
        { key: '/bale-mgmt/laydown-execution', label: 'Laydown Execution',  icon: <PlayCircleOutlined />, permission: 'bale.read' },
        { key: '/bale-mgmt/chute-feed',        label: 'Chute Feed Monitor', icon: <ControlOutlined />,    permission: 'bale.read' },
        // Sprint Costing — lazy re-weigh capture for operators who skipped
        // entering actual weight at scan time.
        { key: '/bale-mgmt/pending-reweigh',   label: 'Pending Re-weigh',   icon: <ClockCircleOutlined />, permission: 'bale.read' },
      ]},
      // ── Dashboards ───────────────────────────────────────────────────────
      { key: 'bm-grp-dash', type: 'group', label: 'Dashboards', children: [
        { key: '/bale-mgmt/quality-posture',   label: 'Quality Posture',    icon: <DotChartOutlined />,   permission: 'bale.read' },
        { key: '/bale-mgmt/lot-performance',   label: 'Lot Performance',    icon: <RiseOutlined />,       permission: 'bale.read' },
        { key: '/bale-mgmt/floor-layout',      label: 'Floor Layout',       icon: <AppstoreOutlined />,   permission: 'bale.read' },
        { key: '/bale-mgmt/bale-ageing',       label: 'Bale Ageing',        icon: <ClockCircleOutlined />, permission: 'bale.read' },
        { key: '/bale-mgmt/compare-laydowns',  label: 'Compare Laydowns',   icon: <SwapOutlined />,       permission: 'bale.read' },
        // Sprint Costing — Layer 4 dashboards
        { key: '/bale-mgmt/daily-mixing-cost', label: 'Daily Mixing Cost',  icon: <DollarOutlined />,     permission: 'bale.read' },
        { key: '/bale-mgmt/cost-variance',     label: 'Cost Variance',      icon: <FundOutlined />,       permission: 'bale.read' },
        // Bales Runout Report — GM/QC review of purchase-vs-consumption
        // deltas. Auto-populates as lots run out.
        { key: '/bale-mgmt/bales-runout-report', label: 'Bales Runout Report', icon: <ExperimentOutlined />, permission: 'bale.read' },
        // Sprint 0072 — Reconciliation: cotton issued at laydown vs
        // cotton consumed by cards (sliver out + waste). Two-stage
        // close, ±0.5%/1.5% thresholds, force-close with audit.
        { key: '/bale-mgmt/reconciliation', label: 'Laydown Reconciliation', icon: <FundOutlined />, permission: 'bale.read' },
      ]},
      // ── Masters ──────────────────────────────────────────────────────────
      { key: 'bm-grp-master', type: 'group', label: 'Masters', children: [
        { key: '/bale-mgmt/yarn-recipes',      label: 'Yarn Quality Targets', icon: <FileTextOutlined />,   permission: 'recipe.read' },
        { key: '/bale-mgmt/cotton-grades',     label: 'Cotton Grades',        icon: <ExperimentOutlined />, permission: 'bale.read' },
        { key: '/bale-mgmt/mixing-recipes',    label: 'Mixing Recipes',       icon: <FileTextOutlined />,   permission: 'bale.read' },
      ]},
    ],
  },

  // Waste & Cost — moved out of Bale Management for cleaner IA
  {
    key: 'waste-cost', icon: <DeleteOutlined />, label: 'Waste & Cost',
    anyOf: ['waste.read'],
    children: [
      { key: '/bale-mgmt/waste-categories',  label: 'Waste Categories',   icon: <DeleteOutlined />,     permission: 'waste.read' },
      { key: '/bale-mgmt/waste-stock',       label: 'Waste Stock',        icon: <DatabaseOutlined />,   permission: 'waste.read' },
      { key: '/bale-mgmt/stage-costs',       label: 'Stage Conv. Costs',  icon: <CalculatorOutlined />, permission: 'waste.read' },
    ],
  },

  {
    key: 'quality', icon: <SafetyCertificateOutlined />, label: 'Quality',
    anyOf: ['quality.read', 'capa.read'],
    children: [
      { key: '/quality/rm',          label: 'RM Cotton Quality',    icon: <FileSearchOutlined />,  permission: 'quality.read' },
      { key: '/quality/moisture',    label: 'Moisture Entry',       icon: <ExperimentOutlined />,  permission: 'quality.read' },
      { key: '/quality/parakh-qc',   label: 'Parakh / Moisture QC', icon: <ExperimentOutlined />,  permission: 'quality.read' },
      { key: '/quality/tests',       label: 'Process Lab Tests',    icon: <AuditOutlined />,       permission: 'quality.read' },
      { key: '/quality/stock-dwell', label: 'Stock & Truck Dwell',  icon: <SafetyOutlined />,      permission: 'quality.read' },
      { key: '/capa/records',        label: 'CAPA & Complaints',    icon: <WarningOutlined />,     permission: 'quality.read' },
    ],
  },

  // ─── SHOP FLOOR — blue-collar entry surface ──────────────────────
  //
  // The operator's home. Daily Shift Log sits at the top (PROD-001A
  // auto-draft covers every dept uniformly), then the role-specific
  // checks that don't have an equivalent in the new flow yet —
  // Maintenance + QC daily forms per department. The legacy
  // BR-Production / Carding-Production entry pages still exist at
  // /spinning/{br,carding}/production for direct-URL access, but they
  // are deliberately removed from the menu — operators now use Daily
  // Shift Log which auto-fetches plan/SO/opening-stock instead of
  // asking the operator to type them.
  {
    key: 'shopfloor', icon: <ThunderboltOutlined />, label: 'Shop Floor',
    anyOf: ['production.read', 'production.create', 'quality.create'],
    children: [
      // ★ The one screen 90% of operators need
      { key: '/production/daily-log',           label: 'Daily Shift Log',
        icon: <ThunderboltOutlined />, permission: 'production.read' },

      // Maintenance daily check sheets — distinct from production logs
      { key: 'sf-maint-grp', type: 'group', label: 'Maintenance Checks', children: [
        { key: '/spinning/blow-room/maintenance', label: 'Blow Room',
          icon: <ToolOutlined />, permission: 'production.create' },
        { key: '/spinning/carding/maintenance',   label: 'Carding',
          icon: <ToolOutlined />, permission: 'production.create' },
      ]},

      // Quality samples — feed yarn_test_results / chute_feed_tests
      { key: 'sf-qc-grp', type: 'group', label: 'Quality Entry', children: [
        { key: '/spinning/blow-room/qc',          label: 'Blow Room QC',
          icon: <ExperimentOutlined />, permission: 'quality.create' },
        { key: '/spinning/carding/qc',            label: 'Carding QC (bulk)',
          icon: <ExperimentOutlined />, permission: 'quality.create' },
      ]},

      // Carding exceptions inbox — operator can ack/resolve their
      // own alerts without leaving Shop Floor
      { key: '/production/carding-alerts',       label: 'Carding Alerts',
        icon: <WarningOutlined />, permission: 'production.read' },
    ],
  },

  // ─── PRODUCTION — manager / planner read-mostly views ────────────
  //
  // Same backend tables as Shop Floor; different audience. Anyone with
  // production.read but not production.create lands here and never
  // sees the daily entry forms (they wouldn't be able to submit anyway).
  {
    key: 'production', icon: <ThunderboltOutlined />, label: 'Production',
    anyOf: ['production.read'],
    children: [
      // Manager dashboards first
      { key: 'prod-grp-dash', type: 'group', label: 'Dashboards & Reports', children: [
        // Sprint 0143 — Unified production MIS (PREP-12)
        { key: '/production/mis',                   label: 'Production MIS',
          icon: <DashboardOutlined />, permission: 'production.read' },
        // Sprint 0143 PREP-14 — Production Reports (8 can-stock + 14 MIS Excel)
        { key: '/production/reports',               label: 'Reports & Exports',
          icon: <FileSearchOutlined />, permission: 'production.read' },
        { key: '/production/sales-order-progress', label: 'SO Progress',
          icon: <FileSearchOutlined />, permission: 'production.read' },
        { key: '/production/section-stock',        label: 'Section Stock',
          icon: <PartitionOutlined />, permission: 'production.read' },
        { key: '/production/logs',                 label: 'Production Logs',
          icon: <ThunderboltOutlined />, permission: 'production.read' },
      ]},

      // Planning + movement
      { key: 'prod-grp-planning', type: 'group', label: 'Planning & Movement', children: [
        { key: '/production/mixing-plans',         label: 'Mixing Plans',
          icon: <MergeCellsOutlined />, permission: 'production.read' },
        { key: '/production/campaigns',            label: 'Production Plan',
          icon: <ScheduleOutlined />, permission: 'production.read' },
        { key: '/production/idt',                  label: 'IDT (Material Transfer)',
          icon: <SwapOutlined />, permission: 'production.read' },
        { key: '/production/productivity',         label: 'Productivity Calculator',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        { key: '/production/waste-disposal',       label: 'Waste Disposal',
          icon: <DeleteOutlined />, permission: 'production.read' },
      ]},

      // Sprint 0146 — Quick Entry from machine displays (Carding + 4 prep)
      { key: 'prod-grp-quick', type: 'group', label: 'Quick Entry', children: [
        { key: '/production/carding-quick-entry',     label: '⚡ Carding Quick Entry',
          icon: <ThunderboltOutlined />, permission: 'production.create' },
        { key: '/production/breaker-df-quick-entry',  label: '⚡ Breaker DF Quick Entry',
          icon: <ThunderboltOutlined />, permission: 'production.create' },
        { key: '/production/finisher-df-quick-entry', label: '⚡ Finisher DF Quick Entry',
          icon: <ThunderboltOutlined />, permission: 'production.create' },
        { key: '/production/lap-former-quick-entry',  label: '⚡ Lap Former Quick Entry',
          icon: <ThunderboltOutlined />, permission: 'production.create' },
        { key: '/production/comber-quick-entry',      label: '⚡ Comber Quick Entry',
          icon: <ThunderboltOutlined />, permission: 'production.create' },
      ]},

      // Masters / setup — least frequent, parked at the bottom
      { key: 'prod-setup-grp', type: 'group', label: 'Setup', children: [
        { key: '/production/count-standards',     label: 'Count Standards',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        // PROD-002D Task 8 — daily bobbin/cop/cone weight override entry
        { key: '/production/daily-package-weight', label: 'Daily Package Weight',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        { key: '/production/carding-settings',    label: 'Carding Settings',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        { key: '/production/carding-waste-studies', label: 'Carding Waste Studies',
          icon: <ExperimentOutlined />, permission: 'production.read' },
        // Sprint 0143 — Preparatory standards (4 separate pages per Option B)
        { key: '/production/breaker-df-settings',  label: 'Breaker DF Settings',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        { key: '/production/finisher-df-settings', label: 'Finisher DF Settings',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        { key: '/production/lap-former-settings',  label: 'Lap Former Settings',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        { key: '/production/comber-settings',      label: 'Comber Settings',
          icon: <CalculatorOutlined />, permission: 'production.read' },
        { key: '/production/checkpoint-frequency',label: 'Checkpoint Frequency',
          icon: <ClockCircleOutlined />, permission: 'production.read' },
      ]},
    ],
  },

  {
    // Sprint 0151 — Authority audit fix.
    // Was gated by report.read which every role with operational reports
    // had (including HR Manager). Traceability is production data:
    // yarn lots back to bales, RM flow from receipt to dispatch. Gate
    // by production.read OR bale.read OR quality.read — production /
    // stores / QC / dispatch see it, HR / finance / payroll do not.
    key: 'traceability', icon: <ApartmentOutlined />, label: 'Traceability',
    anyOf: ['production.read', 'bale.read', 'quality.read'],
    children: [
      { key: '/traceability/yarn-lot',       label: 'Yarn Lot Trace', icon: <SearchOutlined />,    permission: 'bale.read' },
      { key: '/traceability/rm-flow-report', label: 'RM Flow Report', icon: <ApartmentOutlined />, permission: 'bale.read' },
    ],
  },

  // Sprint 0075 Phase 2 — Maintenance role-wise screens
  {
    key: 'maintenance', icon: <ToolOutlined />, label: 'Maintenance',
    anyOf: ['maintenance.read', 'maintenance.create', 'maintenance.update', 'spares.read'],
    children: [
      { key: 'maintenance-ops-grp', type: 'group', label: 'Operations', children: [
        { key: '/maintenance',                 label: 'Maintenance Home',  icon: <DashboardOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/requests',        label: 'Breakdown Requests',icon: <FormOutlined />,      permission: 'maintenance.read' },
        { key: '/maintenance/work-orders',     label: 'Work Order Board',  icon: <SolutionOutlined />,  permission: 'maintenance.read' },
        { key: '/maintenance/my-jobs',         label: 'My Jobs (Tech)',    icon: <ProfileOutlined />,   permission: 'maintenance.read' },
        { key: '/maintenance/spares',          label: 'Spare Desk',        icon: <ShopOutlined />,      anyOf: ['spares.read', 'maintenance.read'] },
        { key: '/maintenance/pm',              label: 'PM Calendar',       icon: <CalendarOutlined />,  permission: 'maintenance.read' },
        { key: '/maintenance/shutdown-plans',  label: 'Shutdown Plans',    icon: <SafetyCertificateOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/utility-logs',    label: 'Utility Running Log', icon: <ThunderboltOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/machine-health',  label: 'Machine Health',    icon: <PieChartOutlined />,  permission: 'maintenance.read' },
      ]},
      { key: 'maintenance-setup-grp', type: 'group', label: 'Setup', children: [
        { key: '/maintenance/masters',         label: 'Maint Masters',     icon: <SettingOutlined />,   permission: 'maintenance.create' },
        { key: '/maintenance/assignment-rules',label: 'Auto-Assignment Rules', icon: <TeamOutlined />,   permission: 'maintenance.create' },
      ]},
      // Phase 3 — dashboards + reports
      { key: 'maintenance-dash-grp', type: 'group', label: 'Dashboards & Reports', children: [
        { key: '/maintenance/manager-dashboard', label: 'Manager Dashboard',     icon: <DashboardOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/plant-head',        label: 'Plant Head Dashboard',  icon: <DashboardOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/reports',           label: 'Reports Index',         icon: <FundOutlined />,      permission: 'maintenance.read' },
        { key: '/maintenance/reports/machine-downtime',       label: 'Machine Downtime',    icon: <BarChartOutlined />,    permission: 'maintenance.read' },
        { key: '/maintenance/reports/department-breakdown',   label: 'Department Breakdown', icon: <ApartmentOutlined />,   permission: 'maintenance.read' },
        { key: '/maintenance/reports/pm-compliance',          label: 'PM Compliance',        icon: <CheckSquareOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/reports/spare-consumption',      label: 'Spare Consumption',    icon: <ShopOutlined />,        permission: 'maintenance.read' },
        { key: '/maintenance/reports/technician-performance', label: 'Technician Perf.',     icon: <TeamOutlined />,        permission: 'maintenance.read' },
        { key: '/maintenance/reports/cost',                   label: 'Maintenance Cost',     icon: <DollarOutlined />,      permission: 'maintenance.read' },
        { key: '/maintenance/reports/repeat-breakdown',       label: 'Repeat Breakdown',     icon: <RetweetOutlined />,     permission: 'maintenance.read' },
        { key: '/maintenance/reports/pending-ageing',         label: 'Pending WO Ageing',    icon: <ClockCircleOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/reports/utility-performance',    label: 'Utility Performance',  icon: <ThunderboltOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/reports/machine-health',         label: 'Machine Health Analytics', icon: <LineChartOutlined />, permission: 'maintenance.read' },
        { key: '/maintenance/reports/cost-per-kg-trend',      label: 'Cost / kg Trend',          icon: <DollarOutlined />,    permission: 'maintenance.read' },
      ]},
      // Phase 3.2-B — Maintenance ↔ Procurement ↔ Accounts
      { key: 'maintenance-procurement-grp', type: 'group', label: 'Procurement Integration', children: [
        { key: '/maintenance/spare-procurement',           label: 'Spare Procurement Pipeline', icon: <NodeIndexOutlined />,     permission: 'maintenance.read' },
        { key: '/maintenance/spare-procurement/auto-pr',   label: 'Raise PR from Work Order',  icon: <FormOutlined />,           permission: 'maintenance.create' },
        { key: '/maintenance/spare-procurement/status',    label: 'WO Procurement Status',     icon: <SearchOutlined />,         permission: 'maintenance.read' },
        { key: '/maintenance/spare-procurement/grn',       label: 'Spare GRN',                 icon: <TruckOutlined />,          anyOf: ['grn.create', 'maintenance.update'] },
        { key: '/maintenance/spare-procurement/invoice',   label: 'Spare Invoice (3-way)',     icon: <FileProtectOutlined />,    anyOf: ['accounting.read', 'maintenance.read'] },
        { key: '/maintenance/spare-procurement/advance',   label: 'PO Advance Payment',        icon: <DollarOutlined />,         anyOf: ['accounting.read', 'maintenance.read'] },
      ]},
    ],
  },

  {
    key: 'hr', icon: <TeamOutlined />, label: 'HR & People',
    anyOf: ['employee.read', 'hr.doc_grace.read'],
    children: [
      // ── DAILY OPERATIONS — top of menu, used every shift ─────────────
      { key: 'hr-grp-ops', type: 'group', label: 'Daily Operations', children: [
        { key: '/hr/worker-allocation',  label: 'Worker Allocation',    icon: <TeamOutlined />,             permission: 'employee.read' },
        // Sprint 0144 ATT — GM approvals for no-gate-log allocations
        { key: '/hr/allocation-gm-approvals', label: 'GM Approvals', icon: <WarningOutlined />, permission: 'employee.create' },
        { key: '/hr/attendance',         label: 'Daily Attendance',     icon: <CheckSquareOutlined />,      permission: 'attendance.read' },
        { key: '/hr/reconciliation',     label: 'Reconciliation',       icon: <SyncOutlined />,             permission: 'attendance.read' },
        { key: '/hr/punch-logs',         label: 'Punching Logs',        icon: <ClockCircleOutlined />,      permission: 'employee.read' },
        { key: '/hr/daily-attendance-report', label: 'Daily Att. Report',   icon: <FileExcelOutlined />,        permission: 'employee.read' },
        { key: '/hr/attendance-variance', label: 'Attendance Variance',   icon: <WarningOutlined />,           permission: 'employee.read' },
        { key: '/hr/worker-skills',       label: 'Worker Skills',          icon: <UserAddOutlined />,           permission: 'employee.read' },
        { key: '/hr/skill-mismatch',      label: 'Skill Mismatch',         icon: <WarningOutlined />,           permission: 'employee.read' },
        { key: '/hr/shift-roster',       label: 'Shift Roster',         icon: <CalendarOutlined />,         permission: 'employee.read' },
        { key: '/hr/welfare-approvals',  label: 'Welfare Approvals',    icon: <CheckSquareOutlined />,      permission: 'employee.update' },
        { key: '/hr/joining',            label: 'Joining',              icon: <UserAddOutlined />,           permission: 'employee.read' },
        // PJD-001 Task 11 — 2nd-ID chase ladder watchlist
        { key: '/hr/doc-grace',          label: 'Document Grace',       icon: <FileProtectOutlined />,      permission: 'hr.doc_grace.read' },
      ]},
      // ── CONTRACTOR & LABOUR — contractor workforce + SLA ──────────────
      { key: 'hr-grp-contractor', type: 'group', label: 'Contractor & Labour', children: [
        { key: '/hr/contractors',        label: 'Contractors',          icon: <ContactsOutlined />,          permission: 'employee.read' },
        { key: '/hr/contractor-headcount-sla', label: 'Headcount SLA',   icon: <AlertOutlined />,             permission: 'contractor.read' },
      ]},
      // ── FOOD STORE — provisions / ration supply (ration = food store) ─
      { key: 'hr-grp-food', type: 'group', label: 'Food Store', children: [
        { key: '/hr/food-store',                   label: 'Dashboard',         icon: <PieChartOutlined />,    permission: 'foodstore.read' },
        { key: '/hr/food-store/items',             label: 'Food Items',        icon: <TagsOutlined />,        permission: 'foodstore.read' },
        { key: '/hr/food-store/issue',             label: 'Issue Food',        icon: <FileDoneOutlined />,    permission: 'foodstore.read' },
        { key: '/hr/food-store/batches',           label: 'Batches & Expiry',  icon: <ClockCircleOutlined />, permission: 'foodstore.read' },
        { key: '/hr/food-store/contractor-ledger', label: 'Contractor Ledger', icon: <BookOutlined />,        permission: 'foodstore.read' },
        { key: '/hr/ration-advances',              label: 'Ration Advances',   icon: <CalendarOutlined />,    permission: 'employee.read' },
      ]},
      // ── SEPARATION & EXIT — F&F, statutory exit, assets, reports ──────
      { key: 'hr-grp-separation', type: 'group', label: 'Separation & Exit', children: [
        { key: '/hr/separations',        label: 'Separations / F&F',    icon: <UserDeleteOutlined />,        permission: 'employee.read' },
        { key: '/hr/finance-posting',    label: 'F&F Finance Posting',  icon: <DollarOutlined />,            permission: 'employee.read' },
        { key: '/hr/statutory-exit',     label: 'Statutory Exit (PF/ESI)', icon: <SafetyCertificateOutlined />, permission: 'employee.read' },
        { key: '/hr/employee-assets',    label: 'Employee Assets',      icon: <AuditOutlined />,             permission: 'employee.read' },
        { key: '/hr/separation-reports', label: 'Separation Reports',   icon: <FileSearchOutlined />,        permission: 'employee.read' },
        { key: '/hr/employee-ledgers',   label: 'Loans / Canteen / Colony', icon: <DollarOutlined />,        permission: 'employee.read' },
      ]},
      // ── WORKFLOWS & APPROVALS — anything that needs sign-off ─────────
      { key: 'hr-grp-flow', type: 'group', label: 'Workflows & Approvals', children: [
        { key: '/hr/my-todo',            label: 'My To-Do',             icon: <CheckSquareOutlined />,      permission: 'employee.read' },
        { key: '/hr/verify-queue',       label: 'Verification Queue',   icon: <CheckCircleOutlined />,      permission: 'employee.read' },
        { key: '/hr/escalation-inbox',   label: 'Escalation Inbox',     icon: <WarningOutlined />,          permission: 'employee.read' },
        { key: '/hr/leave-applications', label: 'Leave Applications',   icon: <FormOutlined />,             permission: 'leave.read' },
        { key: '/hr/leave-balances',     label: 'Leave Balances',       icon: <FallOutlined />,             permission: 'leave.read' },
        { key: '/hr/grievances',         label: 'Grievances',           icon: <ExclamationCircleOutlined />, permission: 'grievance.read' },
        { key: '/hr/task-calendar',      label: 'Task Calendar',        icon: <CalendarOutlined />,         permission: 'employee.read' },
      ]},
      // ── PEOPLE — directory + assessments ─────────────────────────────
      { key: 'hr-grp-people', type: 'group', label: 'People', children: [
        { key: '/hr/employees',          label: 'Employee Master',      icon: <IdcardOutlined />,           permission: 'employee.read' },
        { key: '/hr/team-monitor',       label: 'Team Monitor',         icon: <TeamOutlined />,             permission: 'employee.read' },
        { key: '/hr/org-chart',          label: 'Organisation Chart',   icon: <TeamOutlined />,             permission: 'employee.read' },
        { key: '/hr/reporting-hierarchy-audit', label: 'Hierarchy Audit', icon: <ApartmentOutlined />, permission: 'employee.read' },
        { key: '/hr/my-assessments',     label: 'My Assessments',       icon: <AuditOutlined />,            permission: 'employee.read' },
        { key: '/hr/awards',             label: 'Performance Awards',   icon: <TrophyOutlined />,           permission: 'employee.read' },
      ]},
      // ── STUDIES & REPORTS — floor walks, efficiency, dashboards ──────
      { key: 'hr-grp-studies', type: 'group', label: 'Studies & Reports', children: [
        { key: '/hr/worker-efficiency',  label: 'Worker Efficiency',    icon: <RiseOutlined />,             permission: 'attendance.read' },
        { key: '/hr/training',           label: 'Training',             icon: <BookOutlined />,             permission: 'employee.read' },
        { key: '/hr/production-walk',    label: 'Production Walk',      icon: <FieldTimeOutlined />,        permission: 'employee.read' },
        { key: '/hr/safety-walk',        label: 'EHS Floor Walk',       icon: <SafetyCertificateOutlined />, permission: 'employee.read' },
        { key: '/hr/welfare',            label: 'Welfare Audit',        icon: <HomeOutlined />,             permission: 'welfare.read' },
        { key: '/hr/live-studies',       label: 'Shop-Floor Studies',   icon: <PlayCircleOutlined />,       permission: 'employee.read' },
        { key: '/hr/study-directions',   label: 'Study Directions',     icon: <ThunderboltOutlined />,      permission: 'employee.read' },
        { key: '/hr/executive-dashboard', label: 'Executive Dashboard', icon: <TrophyOutlined />,           permission: 'employee.read' },
      ]},
      // ── CONFIGURATION — master data, set up once ─────────────────────
      { key: 'hr-grp-config', type: 'group', label: 'Configuration', children: [
        { key: '/hr/sanctioned-strength', label: 'Sanctioned Strength', icon: <UsergroupAddOutlined />,     permission: 'employee.read' },
        { key: '/hr/manpower-norms',     label: 'Manpower Norms',       icon: <ApartmentOutlined />,        permission: 'employee.read' },
        { key: '/hr/worker-roles',       label: 'Worker Role Master',   icon: <TeamOutlined />,             permission: 'employee.read' },
        { key: '/hr/skill-matrix',       label: 'Skill / Trade Matrix', icon: <SafetyCertificateOutlined />, permission: 'employee.read' },
        { key: '/hr/role-master',        label: 'Role Responsibility',  icon: <ApartmentOutlined />,        permission: 'employee.read' },
        { key: '/hr/leave-types',        label: 'Leave Types',          icon: <TagsOutlined />,             permission: 'leave.read' },
        { key: '/hr/rule-config',        label: 'Rule Configuration',   icon: <SettingOutlined />,          permission: 'employee.read' },
        { key: '/hr/biometric-devices',  label: 'Biometric Devices',    icon: <CloudServerOutlined />,      permission: 'employee.read' },
        // Phase A — Welfare masters
        { key: '/hr/colonies-rooms',     label: 'Colonies & Rooms',     icon: <HomeOutlined />,             permission: 'employee.read' },
        { key: '/hr/canteen-rules',      label: 'Canteen Rules',        icon: <CoffeeOutlined />,           permission: 'employee.read' },
        { key: '/hr/ration-rules',       label: 'Ration Rules',         icon: <ShoppingCartOutlined />,     permission: 'employee.read' },
        { key: '/hr/certification-expiry', label: 'Certification Expiry', icon: <SafetyCertificateOutlined />, permission: 'employee.read' },
        { key: '/hr/assessment-templates', label: 'Assessment Templates', icon: <FileDoneOutlined />,       permission: 'employee.read' },
        { key: '/hr/assessment-cycles',  label: 'Assessment Cycles',    icon: <CalendarOutlined />,         permission: 'employee.read' },
        { key: '/hr/default-config',     label: 'Default Configuration', icon: <SettingOutlined />,         permission: 'employee.read' },
        { key: '/hr/config-completeness', label: 'Config Completeness', icon: <CheckCircleOutlined />,      permission: 'employee.read' },
      ]},
      // ── ADMIN TOOLS — automation, scheduling ─────────────────────────
      { key: 'hr-grp-tools', type: 'group', label: 'Admin Tools', children: [
        { key: '/hr/scheduler',          label: 'Scheduler',            icon: <ClockCircleOutlined />,      permission: 'employee.read' },
        { key: '/hr/automation',         label: 'Automation Control',   icon: <ThunderboltOutlined />,      permission: 'employee.read' },
        { key: '/hr/attendance-derivation', label: 'Derive Attendance', icon: <ClockCircleOutlined />,      permission: 'employee.read' },
      ]},
    ],
  },

  {
    key: 'payroll', icon: <WalletOutlined />, label: 'Payroll',
    anyOf: ['payroll.read', 'employee.read'],
    children: [
      // ── RUN & OUTPUT — monthly cycle ─────────────────────────────────
      { key: 'pr-grp-run', type: 'group', label: 'Run & Output', children: [
        { key: '/payroll/periods',         label: 'Payroll Periods',    icon: <CalendarOutlined />,         permission: 'payroll.read' },
        { key: '/payroll/run',             label: 'Payroll Run',        icon: <DollarOutlined />,           permission: 'employee.read' },
        { key: '/payroll/payslips',        label: 'Pay Slips',          icon: <PrinterOutlined />,          permission: 'payroll.read' },
        { key: '/payroll/salary-register', label: 'Salary Register',    icon: <FileTextOutlined />,         permission: 'employee.read' },
      ]},
      // ── SETUP — configure once, used every run ───────────────────────
      { key: 'pr-grp-setup', type: 'group', label: 'Setup', children: [
        { key: '/payroll/salary-structures', label: 'Salary Structures', icon: <SettingOutlined />,         permission: 'employee.read' },
        { key: '/payroll/costing',         label: 'Payroll Costing',    icon: <FundOutlined />,             permission: 'employee.read' },
        { key: '/payroll/epf',             label: 'EPF Compliance',     icon: <SafetyCertificateOutlined />, permission: 'employee.read' },
      ]},
    ],
  },

  {
    key: 'fg-sales', icon: <ShoppingOutlined />, label: 'FG & Sales',
    anyOf: ['sales.read', 'fg.read', 'customer.read', 'dispatch.read', 'sales_invoice.read'],
    children: [
      { key: '/fg/store',        label: 'FG Store',     icon: <GoldOutlined />,        permission: 'fg.read' },
      { key: '/sales/customers', label: 'Customers',    icon: <ContactsOutlined />,    permission: 'customer.read' },
      { key: '/sales/orders',    label: 'Sales Orders', icon: <ProfileOutlined />,     permission: 'sales.read' },
      { key: '/sales/stock-build', label: 'Stock-Build Auth', icon: <SafetyCertificateOutlined />, permission: 'sales.read' },
      { key: '/sales/dispatch',  label: 'Dispatch',     icon: <RocketOutlined />,      permission: 'dispatch.read' },
      { key: '/sales/invoices',  label: 'Invoices',     icon: <AccountBookOutlined />, permission: 'sales_invoice.read' },
    ],
  },

  {
    key: 'finance', icon: <BankOutlined />, label: 'Finance & Accounts',
    anyOf: ['accounting.read', 'voucher.read'],
    children: [
      { key: 'fin-grp-ledger', type: 'group', label: 'Dashboard & Ledger', children: [
        { key: '/accounting',               label: 'Finance Dashboard',   icon: <PieChartOutlined />,       permission: 'accounting.read' },
        { key: '/accounting/coa',           label: 'Chart of Accounts',   icon: <ApartmentOutlined />,      permission: 'accounting.read' },
        { key: '/accounting/vouchers',      label: 'Vouchers',            icon: <FileTextOutlined />,       permission: 'voucher.read' },
        { key: '/accounting/trial-balance',    label: 'Trial Balance',       icon: <FundOutlined />,           permission: 'accounting.read' },
      ]},
      { key: 'fin-grp-compliance', type: 'group', label: 'Compliance', children: [
        { key: '/accounting/gst',           label: 'GST Register',        icon: <PercentageOutlined />,     permission: 'accounting.read' },
        { key: '/accounting/tds',           label: 'TDS / TCS',           icon: <FileProtectOutlined />,    permission: 'accounting.read' },
        { key: '/accounting/brs',           label: 'Bank Reconciliation', icon: <ReconciliationOutlined />, permission: 'accounting.read' },
        { key: '/accounting/compliance',    label: 'Compliance & Close',  icon: <CheckCircleOutlined />,    permission: 'accounting.read' },
        { key: '/accounting/monthly-close',    label: 'Month-End Closing',   icon: <LockOutlined />,           permission: 'accounting.read' },
      ]},
      { key: 'fin-grp-payables', type: 'group', label: 'Payables', children: [
        { key: '/accounting/ap-invoice-booking', label: 'Purchase Invoice',   icon: <ShoppingCartOutlined />,   permission: 'voucher.create' },
        { key: '/accounting/tally-import',     label: 'Tally Import',        icon: <ImportOutlined />,         permission: 'voucher.create' },
        { key: '/accounting/bill-wise',        label: 'Bill-wise Outstanding', icon: <AccountBookOutlined />,  permission: 'accounting.read' },
        { key: '/accounting/payment-allocation', label: 'Payment Allocation',   icon: <LinkOutlined />,            permission: 'voucher.create' },
      ]},
      { key: 'fin-grp-reports', type: 'group', label: 'Reports & Assets', children: [
        { key: '/accounting/financial-reports',label: 'Financial Reports',     icon: <FundProjectionScreenOutlined />, permission: 'accounting.read' },
        { key: '/accounting/financial-statements', label: 'P&L · BS · GSTR-3B', icon: <FundOutlined />,            permission: 'accounting.read' },
        { key: '/accounting/standard-costs',   label: 'Standard Costs',      icon: <FundOutlined />,           permission: 'accounting.read' },
        { key: '/accounting/audit-schedules',  label: 'Audit Schedules',     icon: <FileProtectOutlined />,    permission: 'accounting.read' },
        { key: '/accounting/fixed-assets',  label: 'Fixed Assets',        icon: <BuildOutlined />,          permission: 'accounting.read' },
      ]},
    ],
  },

  {
    // Sprint 0151 — Authority leak fix.
    // Was gated by mis.read | report.read, which HR Manager has via the
    // generic MIS_PERMS bundle. That made HR see Debtor Aging, P&L,
    // Balance Sheet — financial reports they have no business seeing.
    // Now gated by accounting.read so only CFO / Accounts / Plant Head
    // sees this section. Each child also re-checks the same perm so
    // direct URL navigation can't bypass it.
    key: 'mis', icon: <FundOutlined />, label: 'MIS & Reports',
    // cost.rate.request.view added so PLANT_HEAD (who lacks
    // accounting.read) can still reach Cost Rate Changes.
    anyOf: ['accounting.read', 'cost.rate.request.view'],
    children: [
      { key: '/mis/dashboard',          label: 'MIS Dashboard',      icon: <PieChartOutlined />,    permission: 'accounting.read' },
      { key: '/mis/profit-loss',        label: 'Profit & Loss',      icon: <RiseOutlined />,        permission: 'accounting.read' },
      { key: '/mis/balance-sheet',      label: 'Balance Sheet',      icon: <FundOutlined />,        permission: 'accounting.read' },
      { key: '/mis/debtor-aging',       label: 'Debtor Aging',       icon: <ClockCircleOutlined />, permission: 'accounting.read' },
      { key: '/mis/creditor-aging',     label: 'Creditor Aging',     icon: <FallOutlined />,        permission: 'accounting.read' },
      { key: '/mis/cost-of-production', label: 'Cost of Production', icon: <DollarOutlined />,      permission: 'accounting.read' },
      { key: '/mis/budget',             label: 'Budget & Variance',  icon: <LineChartOutlined />,   permission: 'accounting.read' },
      { key: '/mis/cash-flow',          label: 'Cash Flow',          icon: <WalletOutlined />,      permission: 'accounting.read' },
      { key: '/mis/landed-cost',        label: 'Landed Cost Report', icon: <DollarOutlined />,      permission: 'accounting.read' },
      // GOV-001 P4 — rate changes need CFO approval; visible to anyone
      // with cost.rate.request.view (Accounts Mgr, CFO, Plant Head, Auditor).
      { key: '/costing/rate-change-requests', label: 'Cost Rate Changes', icon: <AuditOutlined />, permission: 'cost.rate.request.view' },
    ],
  },

  // MIS-001 Slice 1 — Executive Dashboard + Insights Reports (daily digest).
  // Separate from the "MIS & Reports" (accounting.read) group above —
  // these are plant-ops KPIs/exceptions, gated by their own perms so
  // plant-level roles without accounting.read can still see them.
  {
    key: 'insights', icon: <FundOutlined />, label: 'Insights',
    anyOf: ['mis.executive.read', 'mis.reports.read', 'mis.stores.read', 'mis.maintenance.read', 'mis.dashboard.view'],
    children: [
      { key: '/mis/executive', label: 'Executive Dashboard', icon: <DashboardOutlined />, permission: 'mis.executive.read' },
      { key: '/mis/reports',   label: 'Reports',             icon: <BarChartOutlined />,  permission: 'mis.reports.read' },
      { key: '/mis/stores',      label: 'Stores Cockpit',      icon: <FundOutlined />,     permission: 'mis.stores.read' },
      { key: '/mis/maintenance', label: 'Maintenance Cockpit', icon: <AuditOutlined />,     permission: 'mis.maintenance.read' },
      // BI-001 Slice 1 — custom dashboard list + viewer
      { key: '/bi/dashboards', label: 'Dashboards', icon: <AppstoreOutlined />, permission: 'mis.dashboard.view' },
    ],
  },

  {
    // Sprint 0151 — Each report child now uses the permission of its
    // underlying domain instead of generic report.read. HR Manager sees
    // only HR Reports (employee.read), Quality Manager sees only Quality
    // Reports, etc. — instead of every role seeing every report.
    key: 'reports', icon: <BarChartOutlined />, label: 'Reports',
    anyOf: ['report.read', 'employee.read', 'quality.read', 'production.read', 'gate.read'],
    children: [
      { key: '/reports/kpi',        label: 'KPI Dashboard',      icon: <DotChartOutlined />,           permission: 'report.read' },
      { key: '/reports/gate',       label: 'Gate Reports',       icon: <LoginOutlined />,              permission: 'gate.read' },
      { key: '/reports/quality',    label: 'Quality Reports',    icon: <SafetyCertificateOutlined />,  permission: 'quality.read' },
      { key: '/reports/production', label: 'Production Reports', icon: <ThunderboltOutlined />,        permission: 'production.read' },
      // Sprint 0151 — HR-specific reports landing here so HR Manager has
      // a home for their dashboards without seeing financial reports.
      { key: '/reports/hr',         label: 'HR Reports',         icon: <UsergroupAddOutlined />,       permission: 'employee.read' },
    ],
  },

  {
    key: 'approvals', icon: <CheckSquareOutlined />, label: 'Approvals',
    children: [
      // AUTH-001 — unified generic-engine inbox (assignment-based, no perm gate)
      { key: '/approvals/my-pending', label: 'My Approvals', icon: <CheckSquareOutlined /> },
      { key: '/approvals/inbox', label: 'Edit Request Inbox', icon: <AuditOutlined /> },
      {
        key: '/approvals/vendors', label: 'Vendor Approvals', icon: <SafetyOutlined />,
        anyOf: ['supplier.approve_pm', 'supplier.approve_cfo'],
      },
    ],
  },

  {
    key: 'admin', icon: <ControlOutlined />, label: 'Admin',
    anyOf: ['user.read', 'role.read', 'audit.read'],
    children: [
      { key: '/admin/users', label: 'Users',     icon: <UserOutlined />,       permission: 'user.read' },
      { key: '/admin/roles', label: 'Roles',     icon: <SafetyOutlined />,     permission: 'role.read' },
      // GOV-001 P9 — engine matrix editor + governance request browser
      { key: '/admin/approval-matrix', label: 'Approval Matrix', icon: <ControlOutlined />, permission: 'approval_matrix.read' },
      { key: '/admin/approval-requests', label: 'Approval Requests', icon: <FileSearchOutlined />, permission: 'approval_matrix.read' },
      { key: '/admin/audit', label: 'Audit Log', icon: <FileSearchOutlined />, permission: 'audit.read' },
    ],
  },
]

// ── Filter menu items by permissions ──────────────────────────────────────────
// AntD's <Menu items={...}> passes unknown props to the underlying <li>, which
// triggers "React does not recognize the X prop on a DOM element" warnings.
// Strip our custom guards (permission, anyOf) before returning.
function filterMenuItems(items: MenuItemDef[], hasPermission: (p: string) => boolean): MenuItemDef[] {
  return items.reduce<MenuItemDef[]>((acc, item) => {
    // Check item-level permission
    if (item.permission && !hasPermission(item.permission)) return acc
    // Check anyOf — non-empty array: user needs at least one. Empty array: always show.
    if (item.anyOf && item.anyOf.length > 0 && !item.anyOf.some(p => hasPermission(p))) return acc

    // Strip our custom guard props so AntD doesn't forward them to the DOM.
    const { permission: _p, anyOf: _a, children, type, ...rest } = item

    if (children) {
      const filteredChildren = filterMenuItems(children, hasPermission)
      if (filteredChildren.length === 0) return acc // hide section if no children visible
      // Re-attach type only when explicitly set (AntD wants undefined, not null).
      const out: MenuItemDef = { ...rest, children: filteredChildren }
      if (type) out.type = type
      return [...acc, out]
    }

    return [...acc, rest]
  }, [])
}

// ── Open-key helper ────────────────────────────────────────────────────────────
function getOpenKey(path: string): string {
  if (path.startsWith('/masters') || path.startsWith('/gate/suppliers') || path.startsWith('/gate/items') || path.startsWith('/gate/stores')) return 'masters'
  if (path.startsWith('/procurement')) return 'procurement'
  if (path.startsWith('/gate') || path.startsWith('/security')) return 'gate-security'
  if (path.startsWith('/stores')) return 'stores'
  if (path.startsWith('/bale-mgmt')) return 'bale-mgmt'
  if (path.startsWith('/quality') || path.startsWith('/capa')) return 'quality'
  if (path.startsWith('/production')) return 'production'
  if (path.startsWith('/traceability')) return 'traceability'
  if (path.startsWith('/hr')) return 'hr'
  if (path.startsWith('/payroll')) return 'payroll'
  if (path.startsWith('/fg') || path.startsWith('/sales')) return 'fg-sales'
  if (path.startsWith('/accounting')) return 'finance'
  if (path.startsWith('/mis')) return 'mis'
  if (path.startsWith('/reports')) return 'reports'
  if (path.startsWith('/admin')) return 'admin'
  if (path.startsWith('/approvals')) return 'approvals'
  return ''
}

// ── Logo block ─────────────────────────────────────────────────────────────────
function SidebarLogo({ collapsed }: { collapsed: boolean }) {
  return (
    <div style={{
      height: 56,
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      padding: collapsed ? 0 : '0 16px',
      gap: 10,
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      flexShrink: 0,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: 'linear-gradient(135deg, #4096ff, #0958d9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <span style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>S</span>
      </div>
      {!collapsed && (
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
            Spinning Mill
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, lineHeight: 1.2 }}>
            ERP System
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sidebar inner menu (reused in both Sider and Drawer) ───────────────────────
function SidebarMenu({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const hasPermission = useAuthStore(s => s.hasPermission)
  const openKey = getOpenKey(location.pathname)

  const visibleItems = filterMenuItems(menuItems, hasPermission)

  const handleClick = ({ key }: { key: string }) => {
    navigate(key)
    onNavigate?.()
  }

  return (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      defaultOpenKeys={openKey ? [openKey] : []}
      inlineCollapsed={collapsed}
      items={visibleItems as never}
      onClick={handleClick}
      style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        border: 'none',
      }}
    />
  )
}

// ── Props ──────────────────────────────────────────────────────────────────────
interface SidebarProps {
  isMobile: boolean
  drawerOpen: boolean
  onDrawerClose: () => void
  collapsed: boolean
  onCollapse: (v: boolean) => void
}

// ── Mobile Drawer with user panel ─────────────────────────────────────────────
import { Avatar as AntAvatar } from 'antd'

function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const initials = (user?.full_name ?? user?.username ?? 'U')
    .split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={300}
      styles={{
        body: { padding: 0, background: '#001529', display: 'flex', flexDirection: 'column', height: '100%' },
        header: { display: 'none' },
        wrapper: { boxShadow: '4px 0 24px rgba(0,0,0,0.3)' },
      }}
      closable={false}
    >
      {/* User panel */}
      <div className="drawer-user-panel" style={{
        background: 'linear-gradient(135deg, #0958d9 0%, #1677ff 60%, #4096ff 100%)',
        padding: '24px 16px 16px',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <AntAvatar
            size={48}
            style={{
              background: 'rgba(255,255,255,0.25)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              border: '2px solid rgba(255,255,255,0.5)',
            }}
          >
            {initials}
          </AntAvatar>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              {user?.full_name ?? user?.username}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
              @{user?.username}
            </div>
          </div>
        </div>
        {/* App name */}
        <div style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{
            width: 26, height: 26, borderRadius: 6,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 11 }}>SM</span>
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 12, lineHeight: 1.1 }}>Spinning Mill ERP</div>
            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>Management System</div>
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="erp-sidebar-menu" style={{ flex: 1, overflowY: 'auto' }}>
        <SidebarMenu
          collapsed={false}
          onNavigate={() => { onClose(); }}
        />
      </div>
    </Drawer>
  )
}

// ── Main Sidebar export ────────────────────────────────────────────────────────
export default function AppSidebar({
  isMobile, drawerOpen, onDrawerClose, collapsed, onCollapse,
}: SidebarProps) {

  if (isMobile) {
    return <MobileDrawer open={drawerOpen} onClose={onDrawerClose} />
  }

  return (
    <Sider
      collapsed={collapsed}
      onCollapse={onCollapse}
      collapsible
      width={220}
      collapsedWidth={80}
      style={{
        position: 'fixed',
        top: 0, left: 0,
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: 200,
        boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
      }}
      trigger={null}
    >
      <SidebarLogo collapsed={collapsed} />
      <div
        onClick={() => onCollapse(!collapsed)}
        style={{
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-end',
          paddingRight: collapsed ? 0 : 12,
          cursor: 'pointer',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          color: 'rgba(255,255,255,0.35)',
          fontSize: 11,
          userSelect: 'none',
          transition: 'all 0.2s',
        }}
      >
        {collapsed ? '›' : '‹ Collapse'}
      </div>
      <div className="erp-sidebar-menu" style={{ flex: 1, overflowY: 'auto' }}>
        <SidebarMenu collapsed={collapsed} />
      </div>
    </Sider>
  )
}

// ── Bottom Tab Bar (mobile only) — permission-aware ────────────────────────────
const ALL_BOTTOM_TABS = [
  { key: '/',                label: 'Home',    icon: <DashboardOutlined />,  permission: null },
  { key: '/gate/entries',    label: 'Gate',    icon: <LoginOutlined />,      permission: 'gate.read' },
  { key: '/stores/grn',      label: 'Stores',  icon: <DatabaseOutlined />,   permission: 'grn.read' },
  // Operators on mobile land on Daily Shift Log — the auto-draft +
  // checkpoint screen — not the manager Production Logs page.
  { key: '/production/daily-log', label: 'Shop Floor', icon: <ThunderboltOutlined />, permission: 'production.read' },
  { key: '__more__',         label: 'More',    icon: <AppstoreAddOutlined />, permission: null },
]

export function BottomTabBar({ onMoreClick }: { onMoreClick: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const hasPermission = useAuthStore(s => s.hasPermission)

  // Filter tabs by permission, always keep Home and More
  const visibleTabs = ALL_BOTTOM_TABS.filter(
    tab => tab.permission === null || hasPermission(tab.permission)
  )

  // If fewer than 2 functional tabs, just show Home + More
  const tabs = visibleTabs.length >= 2 ? visibleTabs : [ALL_BOTTOM_TABS[0], ALL_BOTTOM_TABS[ALL_BOTTOM_TABS.length - 1]]

  return (
    <div style={{
      position: 'fixed',
      bottom: 0, left: 0, right: 0,
      background: 'rgba(255,255,255,0.97)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid rgba(0,0,0,0.06)',
      display: 'flex',
      zIndex: 500,
      boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {tabs.map(tab => {
        const isActive = tab.key !== '__more__' && location.pathname === tab.key
        return (
          <button
            key={tab.key}
            onClick={() => tab.key === '__more__' ? onMoreClick() : navigate(tab.key)}
            className={isActive ? 'bottom-tab-active' : ''}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '10px 0 8px',
              WebkitTapHighlightColor: 'transparent',
              position: 'relative',
            }}
          >
            {isActive && (
              <div style={{
                position: 'absolute',
                top: 0,
                width: 28,
                height: 3,
                background: 'linear-gradient(90deg, #0958d9, #1677ff)',
                borderRadius: '0 0 4px 4px',
              }} />
            )}
            <div style={{
              width: 40, height: 28,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 14,
              background: isActive ? 'rgba(22,119,255,0.1)' : 'transparent',
              transition: 'background 0.2s',
              fontSize: 18,
              color: isActive ? '#1677ff' : '#8c8c8c',
            }}>
              {tab.icon}
            </div>
            <span style={{
              fontSize: 10,
              fontWeight: isActive ? 700 : 400,
              color: isActive ? '#1677ff' : '#8c8c8c',
              letterSpacing: isActive ? '0.2px' : 0,
              transition: 'all 0.15s',
            }}>
              {tab.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
